import { useMemo } from 'react';
import { useTheme } from '../../theme';
import { useUIStore } from '../../store/uiStore';
import { useDefensePlansStore } from '../../store/defensePlansStore';
import { computeFeatureLabels } from '../../utils/planFeatureLabels';
import { Popover } from '../common/Popover';
import { DEFENSE_STRATEGIES, copyTargetStrategies } from './defenseStrategies';
import {
  CopyMenuRow,
  PlanFeatureMenuItems,
  PlanMenuCaption,
  PlanMenuDivider,
  PlanMenuSurface,
  type PlanFeatureMenu,
} from './DefensePlanFeatureRow.web';
import type { DefensePlanFeature, DefenseStrategy } from '../../types';

/**
 * The menu a right click on a defense shape opens at the cursor. Reads the
 * uiStore `planFeatureContextMenu` slice (set by useDefensePlanLayer) and
 * renders nothing while it is null. Copy targets come first, then the same
 * bundle and Delete items the row's ⋯ menu shows (PlanFeatureMenuItems), so
 * both menus run through the panel's one set of handlers. Popover anchors
 * to a zero-size virtual rect at the click point; it already closes on
 * Escape and on a pointerdown outside.
 */
export function PlanFeatureContextMenu({
  menuFor,
  onCopyAs,
  onDelete,
}: {
  menuFor: (feature: DefensePlanFeature) => PlanFeatureMenu;
  onCopyAs: (feature: DefensePlanFeature, strategy: DefenseStrategy) => void;
  onDelete: (feature: DefensePlanFeature) => boolean | void;
}) {
  const { isDark } = useTheme();
  const open = useUIStore((s) => s.planFeatureContextMenu);
  const features = useDefensePlansStore((s) => s.ownPlan?.features);
  const labels = useMemo(
    () => computeFeatureLabels(features ?? [], (s) => DEFENSE_STRATEGIES[s].label),
    [features]
  );
  const x = open?.x ?? 0;
  const y = open?.y ?? 0;
  const anchorRef = useMemo(
    () => ({
      current: {
        getBoundingClientRect: () => ({
          top: y,
          left: x,
          right: x,
          bottom: y,
          width: 0,
          height: 0,
          x,
          y,
        }),
      },
    }),
    [x, y]
  );
  const feature = open ? features?.find((f) => f.id === open.featureId) : undefined;
  if (!open || !feature) return null;
  const id = feature.id;
  const label = labels[id] ?? DEFENSE_STRATEGIES[feature.strategy].label;
  const menu = menuFor(feature);
  const close = () => useUIStore.getState().closePlanFeatureContextMenu();
  const targets = menu.selection ? [] : copyTargetStrategies(feature);
  return (
    <Popover
      visible
      anchorRef={anchorRef}
      onClose={close}
      placement="bottom-start"
      gap={0}
      testID={`plan-feature-ctx-${id}-menu`}
      accessibilityLabel={`${label} actions`}>
      <PlanMenuSurface>
        {targets.length > 0 && (
          <>
            <PlanMenuCaption>Copy as</PlanMenuCaption>
            {targets.map((strategy) => (
              <CopyMenuRow
                key={strategy}
                strategy={strategy}
                isDark={isDark}
                testID={`plan-feature-ctx-${id}-copy-${strategy}`}
                onPress={() => {
                  close();
                  onCopyAs(feature, strategy);
                }}
              />
            ))}
            <PlanMenuDivider />
          </>
        )}
        <PlanFeatureMenuItems
          feature={feature}
          menu={menu}
          idPrefix={`plan-feature-ctx-${id}`}
          onClose={close}
          onDelete={() => onDelete(feature)}
        />
      </PlanMenuSurface>
    </Popover>
  );
}
